import { Directive, ElementRef, EventEmitter, forwardRef, HostListener, inject, Output, Renderer2 } from '@angular/core';
import { ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { StringDEFromNumberPipe } from '@shared/util-locale';
import { Aenderung } from 'lion-api';

@Directive({
  selector: '[appAmountInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AmountInputDirective),
      multi: true
    }
  ],
  standalone: false
})
export class AmountInputDirective implements ControlValueAccessor {
  private static readonly GELD_FORMAT_PATTERN = /^[+-]?(\d{1,3})(\.\d{3})*,\d{2}$/;
  private static readonly BETRAG_PATTERN = /^[+-]?(\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?|\d+(?:,\d{1,2})?|,\d+)[mMtTkK]?$/;
  private static readonly FAKTOR_1000 = 1000;
  private static readonly FAKTOR_1000000 = 1000000;
  private static readonly THOUSAND_CHAR = 't';
  private static readonly KILO_CHAR = 'k';
  private static readonly MILLION_CHAR = 'm';
  @Output() invalidInput = new EventEmitter<boolean>();
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private stringDEFromNumberPipe = inject(StringDEFromNumberPipe);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onChange: ((value: any) => void) | undefined;
  private onTouched: (() => void) | undefined;
  private previousValue = '';
  private scaleVorzeichenFlag = 0;
  private isDisabled = false;
  //Prüfen, ob manuell gesetzter Wert oder nicht
  private isManualInput: boolean | undefined;

  private controlContainer = inject(ControlContainer, { optional: true });

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.isAllowedKey(event)) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const copyValue = event.clipboardData?.getData('text');
    if (copyValue && !/^[0-9tTmMkK,.]*$/.test(copyValue)) {
      this.invalidInput.emit(true);
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (this.isManualInput || this.isManualInput === undefined) {
      const numericValue = this.transform(value);
      this.onChange!(numericValue);
    }
  }

  @HostListener('focus')
  onFocus(): void {
    this.previousValue = this.el.nativeElement.value;
    this.isManualInput = false;
  }

  @HostListener('blur')
  onBlur(): void {
    const rawValue: string = this.el.nativeElement.value;
    const transformedValue = this.transform(rawValue);
    if (transformedValue === null) {
      console.log('Eingabefehler: Ungültiges Betragsformat');
    }
    this.writeValue(transformedValue);
    this.onChange!(transformedValue);
    this.onTouched!();
    this.isManualInput = true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(value: any): void {
    const displayValue = this.stringDEFromNumberPipe.transform(value);
    this.renderer.setProperty(this.el.nativeElement, 'value', displayValue);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.el.nativeElement.disabled = isDisabled;
  }

  private transform(input: string): number | null {
    if (!input) return null;

    if (AmountInputDirective.BETRAG_PATTERN.test(input)) {
      this.invalidInput.emit(false);
      input = this.behandleVorzeichen(input);
      input = this.behandleKuerzel(input, AmountInputDirective.MILLION_CHAR, AmountInputDirective.FAKTOR_1000000);
      input = this.behandleKuerzel(input, AmountInputDirective.THOUSAND_CHAR, AmountInputDirective.FAKTOR_1000);
      input = this.behandleKuerzel(input, AmountInputDirective.KILO_CHAR, AmountInputDirective.FAKTOR_1000);
    } else {
      this.invalidInput.emit(true);
      return null;
    }

    let result = this.parseNumber(input);
    result = parseFloat(result!.toFixed(2));

    return result;
  }

  private behandleVorzeichen(value: string): string {
    this.scaleVorzeichenFlag = 0;

    if (value.startsWith('+')) {
      this.scaleVorzeichenFlag = 1;
      const changeType = Aenderung.Erhoehen;
      this.updateChangeControl(changeType);
      return value.substring(1);
    } else if (value.startsWith('-')) {
      this.scaleVorzeichenFlag = -1;
      const changeType = Aenderung.Reduzieren;
      this.updateChangeControl(changeType);
      return value.substring(1);
    }
    return value;
  }

  private updateChangeControl(changeType: Aenderung) {
    if (this.controlContainer?.control?.get('change')) {
      this.controlContainer?.control.get('change')?.patchValue(changeType, { emitEvent: false });
    }
  }

  private behandleKuerzel(value: string, kuerzel: string, kuerzelFaktor: number): string {
    if (value.toLowerCase().endsWith(kuerzel)) {
      const newValue = value.slice(0, -1);
      const numberValue = this.parseNumber(newValue)! * kuerzelFaktor;
      return numberValue.toString();
    }
    return value;
  }

  private parseNumber(value: string): number | null {
    if (AmountInputDirective.GELD_FORMAT_PATTERN.test(value)) {
      return parseFloat(value.replaceAll('.', '').replace(',', '.'));
    }
    const normalizedValue = value.replace('.', '').replace(',', '.');
    const parsed = parseFloat(normalizedValue);
    if (isNaN(parsed)) {
      this.invalidInput.emit(true);
      return null;
    }
    return parsed;
  }

  private isAllowedKey(event: KeyboardEvent): boolean {
    const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Backspace', 'Tab', 'Home', 'End', 'Delete', ',', '.', '+', '-'];
    if (allowedKeys.includes(event.key) || event.ctrlKey) return true;
    if (/^[0-9mtk]$/i.test(event.key)) return this.decimalPlacesLength(event.key) <= 2;
    return false;
  }

  private decimalPlacesLength(newValue: string): number {
    if (newValue.includes(',')) return newValue.split(',')[1]?.length || 0;
    if (newValue.includes('.')) return newValue.split('.')[1]?.length || 0;
    return 0;
  }
}
